import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getClassicSpotifyAlbums } from "@/lib/classic-spotify.functions";
import { albumSlug } from "@/lib/slugify";
import { Navbar1 } from "@/components/ui/navbar-1";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/discos-de-vinil/$slug")({
  loader: async ({ params, context }) => {
    const albums = await context.queryClient.ensureQueryData({
      queryKey: ["spotify", "classics"],
      queryFn: () => getClassicSpotifyAlbums(),
      staleTime: 1000 * 60 * 60 * 6,
    });
    const album = albums.find((a) => albumSlug(a.artist, a.title) === params.slug);
    if (!album) throw notFound();
    return { album };
  },
  head: ({ params, loaderData }) => {
    const a = loaderData?.album;
    const url = `https://viny.club/discos-de-vinil/${params.slug}`;
    if (!a) {
      return {
        meta: [{ title: "Disco de vinil — Viny Club" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${a.title} — ${a.artist} | Ficha do disco de vinil`;
    const description = `Ficha técnica do disco de vinil ${a.title} de ${a.artist}${a.year ? ` (${a.year})` : ""}${a.label ? `, gravadora ${a.label}` : ""}. Catalogue na comunidade para colecionadores de vinil do Viny Club.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "music.album" },
        { property: "og:image", content: a.cover },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: a.cover },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MusicAlbum",
            name: a.title,
            byArtist: { "@type": "MusicGroup", name: a.artist },
            datePublished: a.year ? String(a.year) : undefined,
            recordLabel: a.label || undefined,
            genre: a.genres?.length ? a.genres : undefined,
            image: a.cover,
            url,
            albumProductionType: "https://schema.org/StudioAlbum",
            albumReleaseType: "https://schema.org/AlbumRelease",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Início", item: "https://viny.club/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Discos de vinil",
                item: "https://viny.club/discos-de-vinil",
              },
              { "@type": "ListItem", position: 3, name: `${a.title} — ${a.artist}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AlbumDetail,
});

function AlbumDetail() {
  const { slug } = Route.useParams();
  const fn = useServerFn(getClassicSpotifyAlbums);
  const { data: albums = [] } = useQuery({
    queryKey: ["spotify", "classics"],
    queryFn: () => fn(),
    staleTime: 1000 * 60 * 60 * 6,
  });
  const a = albums.find((x) => albumSlug(x.artist, x.title) === slug);
  if (!a) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />
      <main className="mx-auto max-w-5xl px-6 pt-28 pb-16">
        <nav aria-label="Breadcrumb" className="mb-8 text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">
            Início
          </Link>{" "}
          ·{" "}
          <Link to="/discos-de-vinil" className="hover:underline">
            Discos de vinil
          </Link>{" "}
          · <span className="text-foreground">{a.title}</span>
        </nav>

        <div className="grid gap-10 md:grid-cols-[minmax(0,380px)_1fr]">
          <div className="aspect-square overflow-hidden rounded-lg bg-muted">
            <img loading="lazy" decoding="async"
              src={a.cover}
              alt={`Capa do disco de vinil ${a.title} de ${a.artist}`}
              className="h-full w-full object-cover"
            />
          </div>

          <article>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Disco de vinil
            </p>
            <h1 className="mt-2 text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              {a.title}
            </h1>
            <p className="mt-1 text-lg text-muted-foreground">{a.artist}</p>

            <dl className="mt-8 grid grid-cols-2 gap-y-4 text-sm">
              {a.year ? (
                <>
                  <dt className="text-muted-foreground">Ano</dt>
                  <dd>{a.year}</dd>
                </>
              ) : null}
              {a.label ? (
                <>
                  <dt className="text-muted-foreground">Gravadora</dt>
                  <dd>{a.label}</dd>
                </>
              ) : null}
              {a.genres?.length ? (
                <>
                  <dt className="text-muted-foreground">Gêneros</dt>
                  <dd>{a.genres.join(", ")}</dd>
                </>
              ) : null}
              <dt className="text-muted-foreground">Formato</dt>
              <dd>Vinil (LP)</dd>
            </dl>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/auth"
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Adicionar à minha coleção
              </Link>
              <Link
                to="/discos-de-vinil"
                className="inline-flex items-center justify-center rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Ver mais clássicos
              </Link>
            </div>

            <p className="mt-8 text-sm text-muted-foreground">
              {a.title} é um clássico catalogado pela comunidade para colecionadores de vinil do
              Viny Club. Registre sua prensagem, anote a condição e descubra outros donos do mesmo
              disco.
            </p>
          </article>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
